import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import {
  MktCard,
  MktButton,
  StatusBadge,
  Eyebrow,
  studyHref,
  studyStatus,
  type StudyKind,
  TOUCH_TARGET,
} from './marketplaceUi';
import { UseCaseTile, shortLabelForUseCase } from './useCaseMeta';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import { listStudies, dollars, type StudyHistoryItem } from './marketplaceApi';
import { audienceSummary } from './audienceLabel';

/**
 * A plain-language line under the title for the states where "what now?" isn't
 * obvious. The email promise is real — marketplace_report_tasks sends "Your breakfast
 * study results are ready" on completion — so we can safely make it. Refunds are NOT
 * claimed anywhere: there is no credits system to refund to.
 */
const fmtDate = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/** "2 days ago" — a relative stamp reads faster than a date when scanning a list. */
const fmtRelative = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return fmtDate(iso);
};

const statusNote = (s: StudyHistoryItem): string | null => {
  const { label } = studyStatus(s);
  if (label === 'In progress')
    return 'Running now — you can leave; we’ll email you when it’s ready.';
  if (label === 'Taking longer')
    return 'Taking longer than usual — it’s still running, and we’ll email you when it’s done.';
  if (label === 'Failed') return 'This run couldn’t be completed. Open it to see what happened.';
  if (label === 'Awaiting payment') return 'Checkout wasn’t finished — resume to run this study.';
  return null;
};

const isDraft = (s: StudyHistoryItem) => studyStatus(s).kind === 'draft';

const META_SEP = ' · ';

/** The action a row offers depends on what state it's in — a failed run needs
 *  explaining, a draft needs finishing, a finished one needs reading. */
const rowAction = (kind: StudyKind): string => {
  if (kind === 'draft') return 'Resume ▸';
  if (kind === 'failed') return 'See what happened →';
  if (kind === 'running') return 'View progress →';
  if (kind === 'active') return 'Open chat →';
  return 'View report →';
};

const FILTERS: { key: StudyKind | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'running', label: 'Running' },
  { key: 'active', label: 'Active' },
  { key: 'failed', label: 'Failed' },
  { key: 'draft', label: 'Draft' },
];

const MarketplaceHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useMarketplaceAuth();
  const [items, setItems] = useState<StudyHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StudyKind | 'all'>('all');

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    listStudies()
      .then(setItems)
      .catch(() => setError('We couldn’t load your studies. Please try again.'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Search across everything a person would actually remember about a study: what
  // they called it, what kind it was, and who it ran against.
  const matches = useCallback(
    (s: StudyHistoryItem) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      const haystack = [
        (s as StudyHistoryItem & { name?: string }).name || '',
        shortLabelForUseCase(s.use_case),
        ...(s.audience_labels || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    },
    [query],
  );

  // Search narrows the set; the status chips then slice THAT set — so the chip counts
  // always describe what a click would actually show.
  const searched = useMemo(() => (items || []).filter(matches), [items, matches]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: searched.length };
    searched.forEach((s) => {
      const k = studyStatus(s).kind;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [searched]);

  const visible = useMemo(
    () => (filter === 'all' ? searched : searched.filter((s) => studyStatus(s).kind === filter)),
    [searched, filter],
  );

  const firstName = (user?.name || '').trim().split(/\s+/)[0];

  const open = (s: StudyHistoryItem) => {
    const dest = studyHref(s, s.id);
    navigate(dest.path, dest.state ? { state: dest.state } : undefined);
  };

  /** One study row: icon · title · "use case · audience · twins" · status · price. */
  const Row: React.FC<{ s: StudyHistoryItem }> = ({ s }) => {
    const kind = studyStatus(s).kind;
    const ucLabel = shortLabelForUseCase(s.use_case);
    const studyName = (s as StudyHistoryItem & { name?: string }).name;
    const audience = audienceSummary(s.audience_labels);
    const metaHead = [studyName ? ucLabel : '', audience].filter(Boolean).join(META_SEP);
    const note = statusNote(s);
    return (
      <div
        onClick={() => open(s)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') open(s);
        }}
        className="flex cursor-pointer flex-col gap-3 border-b border-black/[0.06] px-4 py-4 transition-colors last:border-b-0 hover:bg-[#f9fafb] md:flex-row md:items-center md:gap-4 md:px-5"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3 md:contents">
          <UseCaseTile useCaseKey={s.use_case} size={40} />

          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold text-[#111]">
              {studyName || ucLabel}
            </div>
            {/* An UNNAMED study falls back to the use-case as its title, so repeating it as
              the first thing in the subtitle reads as a stutter ("Survey" / "Survey · 20
              twins"). Shortening the label made the repetition shorter, not gone — only
              show the use case here when the title is something else. */}
            {/* Two spans, one line on desktop. Truncating the whole meta on a phone ate the
              part that carries the actual numbers (twin count, queries left) — those sit
              last in the sentence. So the head (use case · audience) keeps truncating,
              while the counts drop to their own line where they always fit. From md up the
              spans go inline inside a truncating parent and the sentence reads as before. */}
            <div className="mt-0.5 text-[13px] text-[#6b7280] md:truncate">
              {metaHead && (
                <span className="block truncate md:inline">
                  {metaHead}
                  <span className="hidden md:inline">{META_SEP}</span>
                </span>
              )}
              <span className="block md:inline">
                {s.twin_count} twins
                {s.use_case === 'chat' && s.queries_allowed != null && (
                  <>
                    {META_SEP}
                    {s.queries_used}/{s.queries_allowed} queries used
                    {(s.queries_remaining || 0) > 0 && (
                      <span className="text-primary">
                        {META_SEP}
                        {s.queries_remaining} left
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
            {note && <div className="mt-1 text-[13px] text-[#9ca3af] md:truncate">{note}</div>}
          </div>
        </div>

        {/* The fixed-width columns keep the desktop list aligned (pills of different
            widths otherwise start at different x, and "$4,955" overflows a narrow price
            column). On a phone there is no room for four columns, so status/price/action
            wrap under the title and the action button spans the width as a thumb target. */}
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 md:flex-nowrap md:justify-start md:gap-4">
          <div className="flex items-center gap-3 md:w-[92px] md:flex-col md:items-start md:gap-1">
            <StatusBadge study={s} />
            <span className="whitespace-nowrap text-[11px] text-[#9ca3af]">
              {fmtRelative(s.created_at)}
            </span>
          </div>
          <span className="whitespace-nowrap text-[13px] tabular-nums text-[#6b7280] md:w-16 md:text-right">
            {isDraft(s) ? 'not paid' : s.is_free ? 'Free' : dollars(s.amount_cents || 0)}
          </span>
          {/* ONE button, not a mobile copy + a desktop copy: duplicating it would put the
              same control in the DOM twice for screen readers. It wraps onto its own full
              width line on a phone (a comfortable thumb target) and sits inline at 120px
              from md up. */}
          <MktButton
            variant="secondary"
            className="order-last w-full basis-full whitespace-nowrap px-0 py-2 text-[13px] md:order-none md:w-[120px] md:basis-auto md:py-1.5"
            onClick={(e) => {
              e.stopPropagation();
              open(s);
            }}
          >
            {rowAction(kind)}
          </MktButton>
        </div>
      </div>
    );
  };

  if (error)
    return (
      <MarketplaceShell maxWidth="max-w-4xl">
        <MktCard className="mt-6 p-8 text-center">
          <p className="text-[13px] text-[#6b6b6b]" role="alert">
            {error}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <MktButton onClick={load}>Try again</MktButton>
            <MktButton variant="secondary" onClick={() => navigate('/marketplace/run')}>
              New study
            </MktButton>
          </div>
        </MktCard>
      </MarketplaceShell>
    );

  return (
    <MarketplaceShell maxWidth="max-w-4xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <Eyebrow>My studies</Eyebrow>
          <h1 className="mt-1 text-[28px] font-semibold text-[#111]">
            {firstName ? `Welcome back, ${firstName}` : 'Every study you’ve run or started'}
          </h1>
          <p className="mt-1.5 text-[13px] text-[#6b7280]">
            Everything you’ve run or started, newest first.
          </p>
        </div>
        <MktButton onClick={() => navigate('/marketplace/run')}>+ New study</MktButton>
      </div>

      <div className="relative mt-5">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your studies by name, use case or audience…"
          aria-label="Search studies"
          className={`w-full rounded-lg border border-black/10 bg-white py-2.5 pl-9 pr-3 text-[13px] outline-none transition-colors placeholder:text-[#9ca3af] focus:border-primary focus:ring-2 focus:ring-primary/20 ${TOUCH_TARGET}`}
        />
      </div>

      {/* Status filter. A chip with nothing behind it is noise, so only statuses the
          user actually HAS are offered (All is always there). */}
      {items && items.length > 0 && (
        <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
          {FILTERS.filter((f) => f.key === 'all' || (counts[f.key] ?? 0) > 0).map((f) => {
            const on = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                aria-pressed={on}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors',
                  TOUCH_TARGET,
                  on
                    ? 'border-primary bg-[#E8FCFA] text-[#0B7C74]'
                    : 'border-black/10 bg-white text-[#6b7280] hover:border-black/25 hover:text-[#111]',
                ].join(' ')}
              >
                {f.label}
                <span className={on ? 'text-[#0B7C74]/70' : 'text-[#9ca3af]'}>
                  {counts[f.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!items ? (
        <div className="mt-8 text-[13px] text-[#6b6b6b]">Loading…</div>
      ) : items.length === 0 ? (
        <MktCard className="mt-6 p-8 text-center">
          <p className="text-[13px] text-[#6b6b6b]">You haven’t run any studies yet.</p>
          <div className="mt-4">
            <MktButton onClick={() => navigate('/marketplace/run')}>
              Run your first study →
            </MktButton>
          </div>
        </MktCard>
      ) : visible.length === 0 ? (
        <MktCard className="mt-6 p-8 text-center">
          <p className="text-[13px] text-[#6b6b6b]">
            {query.trim() ? `No studies match “${query.trim()}”.` : 'No studies with this status.'}
          </p>
          <div className="mt-4">
            <MktButton
              variant="secondary"
              onClick={() => {
                setQuery('');
                setFilter('all');
              }}
            >
              Clear filters
            </MktButton>
          </div>
        </MktCard>
      ) : (
        <MktCard className="mt-5 p-0">
          {/* Every study, always. A "View all" link that hid the rest behind a click
              just made people wonder what they were missing. */}
          <div className="flex items-center justify-between px-5 py-3.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6b7280]">
              {filter === 'all'
                ? 'All studies'
                : `${FILTERS.find((f) => f.key === filter)?.label} studies`}
            </span>
            <span className="text-[12px] text-[#9ca3af]">{visible.length}</span>
          </div>
          <div className="border-t border-black/[0.06]">
            {visible.map((s) => (
              <Row key={s.id} s={s} />
            ))}
          </div>
        </MktCard>
      )}
    </MarketplaceShell>
  );
};

export default MarketplaceHistory;
